import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Mail, PenLine, Download, MessageSquareQuote } from "lucide-react";
import api, { API } from "@/lib/api";
import { analytics } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { ReviewDialog } from "@/components/ReviewDialog";
import { Footer } from "@/components/Footer";

const softDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export default function Journey() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [entries, setEntries] = useState(null);
  const [sending, setSending] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const isMember = user && user !== false && user.subscription_status === "active";

  useEffect(() => {
    analytics.journeyViewed();
    api
      .get("/journey")
      .then(({ data }) => {
        setEntries(data);
        // Show the membership invitation ONCE — only right after the free
        // reflection has just ended and its Journey entry is now visible.
        if (location.state?.justEnded && !isMember && (data || []).length >= 1) {
          setUpgradeOpen(true);
        }
      })
      .catch(() => setEntries([]));
    // clear the navigation flag so the prompt never reappears on later visits
    if (location.state?.justEnded) {
      window.history.replaceState({}, "");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendQuestion = async () => {
    if (sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/daily/send-me");
      if (data.sent) {
        toast(`Sent to ${data.to}: “${data.question}”`);
      } else {
        toast(data.message || "Email is in test mode right now.");
      }
    } catch (err) {
      toast(err.response?.data?.detail || "Could not send right now.");
    } finally {
      setSending(false);
    }
  };

  const downloadBlob = async (url, filename) => {
    try {
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "download failed");
      }
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast(err.message || "Could not prepare the download.");
    }
  };

  const downloadOne = (e) =>
    downloadBlob(`${API}/journey/${e.id}/export`, `mirror_reflection_${e.title}.txt`);

  const downloadAll = () =>
    downloadBlob(`${API}/journey/export/all`, "mirror_journey_all.txt");

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 px-6 sm:px-10 py-8">
      <header className="max-w-3xl mx-auto flex items-center justify-between">
        <button
          data-testid="journey-back-button"
          onClick={() => navigate("/begin")}
          className="flex items-center gap-2 text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500 text-sm"
        >
          <ArrowLeft size={16} /> Begin
        </button>
        <span className="text-xs tracking-[0.35em] uppercase text-[var(--mirror-text-2)]">
          Journey
        </span>
      </header>

      <main className="max-w-3xl mx-auto mt-20">
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight"
        >
          What the mirror has kept.
        </motion.h1>
        <p className="mt-4 text-[var(--mirror-text-3)] text-sm max-w-md">
          A quiet archive of your reflections: the values that surfaced, the
          questions still open, and the small steps left behind.
        </p>

        {entries && entries.length > 0 && (
          <button
            data-testid="journey-download-all-button"
            onClick={downloadAll}
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-[rgba(242,236,228,0.15)] px-6 py-2 text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-2)] hover:text-[var(--mirror-text)] hover:border-[rgba(242,236,228,0.3)] transition-colors duration-500"
          >
            <Download size={14} /> Download all
          </button>
        )}

        {entries && entries.length > 0 && (
          <button
            data-testid="journey-send-question-button"
            onClick={sendQuestion}
            disabled={sending}
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-[rgba(242,236,228,0.15)] px-6 py-2 text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-2)] hover:text-[var(--mirror-text)] hover:border-[rgba(242,236,228,0.3)] transition-colors duration-500 disabled:opacity-40"
          >
            <Mail size={14} /> {sending ? "Sending…" : "Email me one question to sit with"}
          </button>
        )}

        {entries && entries.length > 0 && (
          <button
            data-testid="journey-write-review-button"
            onClick={() => setReviewOpen(true)}
            className="mt-4 sm:mt-8 sm:ml-3 inline-flex items-center gap-2 rounded-full border border-[rgba(242,236,228,0.15)] px-6 py-2 text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-2)] hover:text-[var(--mirror-text)] hover:border-[rgba(242,236,228,0.3)] transition-colors duration-500"
          >
            <PenLine size={14} /> Share your reflection
          </button>
        )}

        {entries === null && (
          <p className="mt-16 text-[var(--mirror-text-3)] text-sm">Gathering…</p>
        )}

        {entries && entries.length === 0 && (
          <div
            data-testid="journey-empty-state"
            className="mt-20 text-center"
          >
            <p className="font-serif-display text-2xl font-light text-[var(--mirror-text-2)]">
              Nothing kept yet.
            </p>
            <p className="mt-4 text-sm text-[var(--mirror-text-3)] max-w-sm mx-auto leading-relaxed">
              A reflection is saved here when you finish a conversation. Open the menu inside
              any chat and choose “Save to your Journey.”
            </p>
            <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
              <button
                data-testid="journey-start-first"
                onClick={() => navigate("/begin")}
                className="px-8 py-3 rounded-full border border-[rgba(242,236,228,0.2)] text-sm tracking-[0.2em] uppercase hover:bg-[rgba(242,236,228,0.06)] transition-colors duration-700"
              >
                Begin a conversation
              </button>
              <button
                data-testid="journey-view-chats"
                onClick={() => navigate("/history")}
                className="px-8 py-3 rounded-full border border-[rgba(242,236,228,0.12)] text-sm tracking-[0.2em] uppercase text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] hover:border-[rgba(242,236,228,0.3)] transition-colors duration-700"
              >
                View your chats
              </button>
            </div>
          </div>
        )}

        <div className="mt-14 flex flex-col gap-6">
          {entries?.map((e, i) => (
            <motion.article
              key={e.id}
              data-testid={`journey-entry-${e.id}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.08 * i }}
              className="rounded-2xl border border-[rgba(242,236,228,0.08)] p-7 sm:p-8"
            >
              <div className="flex items-baseline justify-between gap-4">
                <h2 className="font-serif-display text-2xl font-light text-[var(--mirror-text)]">
                  {e.title}
                </h2>
                <span className="text-xs text-[var(--mirror-text-3)] whitespace-nowrap">
                  {softDate(e.created_at)}
                </span>
              </div>

              {e.summary && (
                <p className="mt-4 text-[var(--mirror-text-2)] leading-relaxed">
                  {e.summary}
                </p>
              )}

              {e.values?.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)]">
                    Values that surfaced
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {e.values.map((v, k) => (
                      <span
                        key={`${e.id}-value-${k}`}
                        className="rounded-full border border-[rgba(242,236,228,0.15)] px-4 py-1 text-sm text-[var(--mirror-text-2)]"
                      >
                        {v}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {e.lingering_questions?.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)]">
                    Lingering questions
                  </p>
                  <ul className="mt-3 space-y-2">
                    {e.lingering_questions.map((q, k) => (
                      <li
                        key={`${e.id}-q-${k}`}
                        className="font-serif-display text-lg font-light italic text-[var(--mirror-text)]"
                      >
                        {q}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {e.small_step && (
                <div className="mt-6 rounded-lg bg-[rgba(140,122,107,0.08)] border border-[rgba(140,122,107,0.2)] px-5 py-4">
                  <p className="text-xs tracking-[0.2em] uppercase text-[var(--mirror-accent)]">
                    A small step
                  </p>
                  <p className="mt-2 text-[var(--mirror-text)] leading-relaxed">
                    {e.small_step}
                  </p>
                </div>
              )}

              <div className="mt-7 flex items-center gap-6">
                <button
                  data-testid={`journey-continue-${e.conversation_id}`}
                  onClick={() => navigate(`/chat/${e.conversation_id}`)}
                  className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500"
                >
                  Revisit this reflection <ArrowRight size={14} />
                </button>
                <button
                  data-testid={`journey-download-${e.id}`}
                  onClick={() => downloadOne(e)}
                  className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500"
                >
                  <Download size={14} /> Download
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </main>

      <ReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        onSubmitted={() => {}}
      />

      {upgradeOpen && (
        <div
          data-testid="journey-upgrade-modal"
          className="fixed inset-0 z-[10000] flex items-center justify-center px-6 bg-black/60 backdrop-blur-sm"
          onClick={() => setUpgradeOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-[var(--mirror-border-strong)] bg-[var(--mirror-bg-2)] p-8 text-center"
          >
            <h3 className="font-serif-display text-2xl font-light text-[var(--mirror-text)]">
              This is where it begins.
            </h3>
            <p className="mt-4 text-sm text-[var(--mirror-text-2)] leading-relaxed">
              You've completed your first reflection — the full loop of question, value, and a
              small step. To keep looking, as often as you need, become a member.
            </p>
            <div className="mt-8 flex flex-col gap-3">
              <button
                data-testid="journey-upgrade-cta"
                onClick={() => navigate("/pricing")}
                className="w-full py-3 rounded-full bg-[var(--mirror-accent)] text-[#16151F] text-sm tracking-[0.2em] uppercase font-medium hover:opacity-90 transition-opacity duration-500"
              >
                Become a member — $9/mo
              </button>
              <button
                data-testid="journey-upgrade-dismiss"
                onClick={() => setUpgradeOpen(false)}
                className="text-sm tracking-wide text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500"
              >
                Not now
              </button>
            </div>
          </motion.div>
        </div>
      )}
      </div>
      <Footer />
    </div>
  );
}
